/**
 * Helper functions for React Query cache updates
 *
 * These functions handle optimistic updates and cache synchronization
 * to avoid unnecessary refetches after mutations.
 */

import { QueryClient } from "@tanstack/react-query";
import {
  BenefitPlanGroup,
  ListBenefitPlanGroupsParams,
  RfpCoverage,
  RfpData,
} from "@platform/plan-grid-core";
import { queryKeys } from "@platform/plan-grid-core";

/**
 * Update RFP coverage data in the query cache
 *
 * @param queryClient - React Query client
 * @param rfpId - RFP identifier
 * @param coverages - Updated coverage data
 */
export const updateRfpCoveragesInCache = (
  queryClient: QueryClient,
  rfpId: string,
  updatedCoverages: RfpCoverage[],
): void => {
  const rfpQueryKey = queryKeys.rfp(rfpId);

  queryClient.setQueryData<RfpData>(rfpQueryKey, (oldData) => {
    if (!oldData) return oldData;

    // Create a map for quick lookup of updated coverages by type and id
    // using `${type}-${id}` as the key for coverages with dual plans like Dental and Vision
    const updatedCoveragesByTypeAndId = new Map(
      updatedCoverages.map((coverage) => [
        `${coverage.type}-${coverage.id}`,
        coverage,
      ]),
    );
    const coverages = oldData.coverages.map((coverage) => {
      const updatedCoverage = updatedCoveragesByTypeAndId.get(
        `${coverage.type}-${coverage.id}`,
      );
      if (!updatedCoverage) {
        return coverage;
      }

      // Merge benefit plans to avoid overwriting unchanged plans
      const updatedBenefitPlansById = new Map(
        updatedCoverage.benefitPlans.map((plan) => [plan.id, plan]),
      );
      const benefitPlans = coverage.benefitPlans.map((plan) => {
        const updatedPlan = updatedBenefitPlansById.get(plan.id);
        return updatedPlan ?? plan;
      });

      // Strip _dirtyPlanIds — it is internal autosave tracking metadata and
      // must not leak into the query cache. If it does, the cached coverage
      // flows back as a prop, resolveCoveragePropChange restores it, and the
      // next autosave re-PATCHes plans that were already saved.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _dirtyPlanIds, ...cleanCoverage } = updatedCoverage;

      return {
        ...cleanCoverage,
        benefitPlans,
      };
    });
    return {
      ...oldData,
      coverages,
    };
  });
};

/**
 * Update a specific coverage in the query cache
 *
 * @param queryClient - React Query client
 * @param rfpId - RFP identifier
 * @param coverageType - Type of coverage to update
 * @param updatedCoverage - Updated coverage data
 */
export const updateCoverageInCache = (
  queryClient: QueryClient,
  rfpId: string,
  coverageType: string,
  updatedCoverage: RfpCoverage,
): void => {
  const coverageQueryKey = queryKeys.coverage({
    rfpId,
    coverageType,
  });

  // Update coverage query cache
  queryClient.setQueryData<RfpCoverage | null>(
    coverageQueryKey,
    updatedCoverage,
  );

  // Also update in RFP cache if it exists
  const rfpQueryKey = queryKeys.rfp(rfpId);
  queryClient.setQueryData<RfpData>(rfpQueryKey, (oldData) => {
    if (!oldData) return oldData;

    const updatedCoverages = oldData.coverages.map((coverage) =>
      coverage.type === coverageType ? updatedCoverage : coverage,
    );

    return {
      ...oldData,
      coverages: updatedCoverages,
    };
  });
};

/**
 * Update a single benefit plan group in the cache
 *
 * @param queryClient - React Query client
 * @param params - Query parameters for the benefit plan groups list
 * @param updatedGroup - Updated benefit plan group
 */
export const updateBenefitPlanGroupInCache = (
  queryClient: QueryClient,
  params: ListBenefitPlanGroupsParams,
  updatedGroup: BenefitPlanGroup,
): void => {
  const queryKey = queryKeys.benefitPlanGroups(params);

  queryClient.setQueryData<BenefitPlanGroup[]>(queryKey, (oldData) => {
    if (!oldData) return oldData;

    return oldData.map((group) =>
      group.id === updatedGroup.id ? updatedGroup : group,
    );
  });
};

/**
 * Update benefit plan groups order in the cache
 *
 * @param queryClient - React Query client
 * @param params - Query parameters for the benefit plan groups list
 * @param reorderedGroups - Reordered benefit plan groups
 */
export const updateBenefitPlanGroupsOrderInCache = (
  queryClient: QueryClient,
  params: ListBenefitPlanGroupsParams,
  reorderedGroups: BenefitPlanGroup[],
): void => {
  const queryKey = queryKeys.benefitPlanGroups(params);

  queryClient.setQueryData<BenefitPlanGroup[]>(queryKey, () => reorderedGroups);
};

/**
 * Add a new benefit plan group to the cache
 *
 * @param queryClient - React Query client
 * @param params - Query parameters for the benefit plan groups list
 * @param newGroup - New benefit plan group
 */
export const addBenefitPlanGroupToCache = (
  queryClient: QueryClient,
  params: ListBenefitPlanGroupsParams,
  newGroup: BenefitPlanGroup,
): void => {
  const queryKey = queryKeys.benefitPlanGroups(params);

  queryClient.setQueryData<BenefitPlanGroup[]>(queryKey, (oldData) => {
    if (!oldData) {
      return [newGroup];
    }

    return [...oldData, newGroup];
  });
};

/**
 * Remove a benefit plan group from the cache
 *
 * @param queryClient - React Query client
 * @param params - Query parameters for the benefit plan groups list
 * @param groupId - ID of the group to remove
 */
export const removeBenefitPlanGroupFromCache = (
  queryClient: QueryClient,
  params: ListBenefitPlanGroupsParams,
  groupId: string,
): void => {
  const queryKey = queryKeys.benefitPlanGroups(params);

  queryClient.setQueryData<BenefitPlanGroup[]>(queryKey, (oldData) => {
    if (!oldData) return oldData;

    return oldData.filter((group) => group.id !== groupId);
  });
};
